import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useComponentSize } from '@carnica/utils/hooks';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { usePopoverReposition } from "../usePopoverReposition";
import { getComputedPositionAndDirection, getElement, getScrollPosition, getViewPort } from "./utils";
export var usePopoverPosition = function usePopoverPosition(params) {
  var container = params.container,
    position = params.position,
    reposition = params.reposition,
    offsetHorizontal = params.offsetHorizontal,
    offsetVertical = params.offsetVertical,
    anchorRef = params.anchorRef,
    equalAnchorWidth = params.equalAnchorWidth,
    coordinatesParam = params.coordinates,
    possibleDirections = params.possibleDirections,
    directionParam = params.direction,
    onChangeDirection = params.onChangeDirection;
  var componentRef = useRef(null);
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    anchorClientRect = _useState2[0],
    setAnchorClientRect = _useState2[1];
  var _useComponentSize = useComponentSize(componentRef),
    width = _useComponentSize.width,
    height = _useComponentSize.height;
  var anchorSize = useComponentSize(anchorRef || {
    current: null
  });
  var previousDirectionRef = useRef(null);
  var _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    bannedDirections = _useState4[0],
    setBannedDirections = _useState4[1];
  var _useState5 = useState(),
    _useState6 = _slicedToArray(_useState5, 2),
    containerClientRect = _useState6[0],
    setContainerClientRect = _useState6[1];
  var resetBannedDirections = function resetBannedDirections() {
    setBannedDirections(function (state) {
      return state.length ? [] : state;
    });
    previousDirectionRef.current = null;
  };
  var updateAnchorClientRect = function updateAnchorClientRect() {
    var _anchorRef$current;
    setAnchorClientRect(anchorRef === null || anchorRef === void 0 ? void 0 : (_anchorRef$current = anchorRef.current) === null || _anchorRef$current === void 0 ? void 0 : _anchorRef$current.getBoundingClientRect());
    var element = getElement(container);
    var _ref = element || {},
      _ref$offsetTop = _ref.offsetTop,
      offsetTop = _ref$offsetTop === void 0 ? 0 : _ref$offsetTop,
      _ref$offsetLeft = _ref.offsetLeft,
      offsetLeft = _ref$offsetLeft === void 0 ? 0 : _ref$offsetLeft;
    var _ref2 = (element === null || element === void 0 ? void 0 : element.getBoundingClientRect()) || {},
      _ref2$x = _ref2.x,
      x = _ref2$x === void 0 ? 0 : _ref2$x,
      _ref2$y = _ref2.y,
      y = _ref2$y === void 0 ? 0 : _ref2$y;
    setContainerClientRect({
      y: (element === null || element === void 0 ? void 0 : element.tagName) === 'BODY' ? offsetTop : y,
      x: (element === null || element === void 0 ? void 0 : element.tagName) === 'BODY' ? offsetLeft : x
    });
  };
  var coords = useMemo(function () {
    return anchorClientRect && containerClientRect ? {
      x: anchorClientRect.left - (position === 'absolute' ? containerClientRect.x : 0),
      y: anchorClientRect.top - (position === 'absolute' ? containerClientRect.y : 0)
    } : coordinatesParam;
  }, [coordinatesParam, containerClientRect, position, anchorClientRect]);
  var _getComputedPositionA = getComputedPositionAndDirection({
      contentSize: {
        width: width,
        height: height
      },
      viewportSize: getViewPort(container),
      offsetHorizontal: offsetHorizontal,
      offsetVertical: offsetVertical,
      direction: directionParam,
      possibleDirections: possibleDirections,
      bannedDirections: bannedDirections,
      coordinates: coords,
      anchorSize: anchorSize
    }),
    coordinates = _getComputedPositionA.coordinates,
    direction = _getComputedPositionA.direction;
  useEffect(updateAnchorClientRect, [anchorSize]);
  usePopoverReposition({
    isActive: !!reposition,
    scrollAnchorRef: anchorRef || {
      current: null
    },
    onRequestReposition: function onRequestReposition() {
      resetBannedDirections();
      updateAnchorClientRect();
    }
  });
  useLayoutEffect(function () {
    if (previousDirectionRef.current !== direction) {
      onChangeDirection === null || onChangeDirection === void 0 ? void 0 : onChangeDirection(direction);
      if (previousDirectionRef.current && !bannedDirections.includes(previousDirectionRef.current) && !bannedDirections.includes(direction)) {
        setBannedDirections(function (state) {
          return previousDirectionRef.current ? [].concat(_toConsumableArray(state), [previousDirectionRef.current]) : state;
        });
      }
      previousDirectionRef.current = direction;
    }
  }, [direction]);
  var notVisible = !coordinates || !height || !width;
  var cssVariables = useMemo(function () {
    var _obj;
    if (notVisible) {
      return {};
    }
    var scrollPosition = getScrollPosition(container);
    var obj = (_obj = {}, _defineProperty(_obj, '--popover-position', position), _defineProperty(_obj, '--popover-top', "".concat(((coordinates === null || coordinates === void 0 ? void 0 : coordinates.y) || 0) + (position === 'absolute' ? scrollPosition.y : 0), "px")), _defineProperty(_obj, '--popover-left', "".concat(((coordinates === null || coordinates === void 0 ? void 0 : coordinates.x) || 0) + (position === 'absolute' ? scrollPosition.x : 0), "px")), _obj);
    if (!coordinates) {
      obj['--popover-visibility'] = 'hidden';
    }
    if (equalAnchorWidth) {
      obj['--popover-width'] = "".concat(anchorSize.width, "px");
    }
    return obj;
  }, [notVisible, coordinates, position, equalAnchorWidth, anchorClientRect]);
  useLayoutEffect(resetBannedDirections, [params]);
  return useMemo(function () {
    return {
      componentRef: componentRef,
      direction: direction,
      notVisible: notVisible,
      cssVariables: cssVariables
    };
  }, [direction, notVisible, cssVariables]);
};